import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Grid, Typography, Skeleton,
  CardActionArea, LinearProgress, Avatar, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BuildIcon from "@mui/icons-material/Build";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PaymentIcon from "@mui/icons-material/Payment";
import BarChartIcon from "@mui/icons-material/BarChart";
import SendIcon from "@mui/icons-material/Send";
import MessageIcon from "@mui/icons-material/Message";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FunctionsIcon from "@mui/icons-material/Functions";
import LinkIcon from "@mui/icons-material/Link";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/PageHeader.js";
import { useApi } from "../hooks/useApi.js";
import { currentPeriod } from "@kappa-v2/contracts";
import type { Partner, PeriodSummary } from "@kappa-v2/contracts";

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function KpiCard({ title, value, subtitle, icon, color, loading }: KpiCardProps) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {title}
          </Typography>
          <Avatar sx={{ bgcolor: color + "18", width: 36, height: 36 }}>
            <Box sx={{ color, display: "flex" }}>{icon}</Box>
          </Avatar>
        </Box>
        {loading ? (
          <Skeleton width={80} height={40} />
        ) : (
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1 }}>
            {value}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Solo le card più usate, nell'ordine richiesto dal cliente (3 righe da 4).
const QUICK_LINKS = [
  // Riga 1
  { titleKey: "nav.partners", descKey: "home.links.partnersDesc", icon: <PeopleIcon />, path: "/protected/partners", color: "#1B5E20" },
  { titleKey: "nav.receipts", descKey: "home.links.receiptsDesc", icon: <ReceiptIcon />, path: "/protected/receipts", color: "#6A1B9A" },
  { titleKey: "nav.statistics", descKey: "home.links.statisticsDesc", icon: <BarChartIcon />, path: "/protected/statistics", color: "#4A148C" },
  { titleKey: "nav.summary", descKey: "home.links.summaryDesc", icon: <AccountBalanceIcon />, path: "/protected/summary", color: "#00695C" },
  // Riga 2
  { titleKey: "nav.creditsGenerate", descKey: "home.links.creditsGenerateDesc", icon: <FunctionsIcon />, path: "/protected/credits-generate", color: "#E65100" },
  { titleKey: "nav.partnerServices", descKey: "home.links.partnerServicesDesc", icon: <LinkIcon />, path: "/protected/partner-services", color: "#0D47A1" },
  { titleKey: "nav.services", descKey: "home.links.servicesDesc", icon: <BuildIcon />, path: "/protected/services", color: "#1565C0" },
  { titleKey: "nav.debits", descKey: "home.links.debitsDesc", icon: <PaymentIcon />, path: "/protected/debits", color: "#004D40" },
  // Riga 3
  { titleKey: "sidebar.composeWa", descKey: "home.links.composeWaDesc", icon: <SendIcon />, path: "/protected/whatsapp/compose", color: "#2E7D32" },
  { titleKey: "sidebar.queuesWa", descKey: "home.links.queuesWaDesc", icon: <FormatListBulletedIcon />, path: "/protected/whatsapp/queues", color: "#1B5E20" },
  { titleKey: "sidebar.messagesWa", descKey: "home.links.messagesWaDesc", icon: <MessageIcon />, path: "/protected/whatsapp/messages", color: "#00695C" },
  { titleKey: "sidebar.templatesWa", descKey: "home.links.templatesWaDesc", icon: <WhatsAppIcon />, path: "/protected/whatsapp/templates", color: "#1B5E20" }
];

// Elenco periodi selezionabili (dal mese corrente, a ritroso ~3 anni), formato "MM/YYYY".
const buildPeriods = (): string[] => {
  const now = new Date();
  const out: string[] = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
    const maxM = y === now.getFullYear() ? now.getMonth() + 1 : 12;
    for (let m = maxM; m >= 1; m--) out.push(`${String(m).padStart(2, "0")}/${y}`);
  }
  return out;
};
const PERIODS = buildPeriods();

const MONTH_KEYS = [
  "form.month.january", "form.month.february", "form.month.march", "form.month.april",
  "form.month.may", "form.month.june", "form.month.july", "form.month.august",
  "form.month.september", "form.month.october", "form.month.november", "form.month.december"
];

export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { call } = useApi();

  const [period, setPeriod] = useState(currentPeriod()); // "MM/YYYY"
  const [partnerCount, setPartnerCount] = useState<number | null>(null);
  const [summary, setSummary] = useState<PeriodSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Etichetta leggibile del periodo selezionato, es. "Giugno 2026" / "June 2026".
  const [mm, yyyy] = period.split("/");
  const currentPeriodLabel = `${t(MONTH_KEYS[(Number(mm) || 1) - 1] ?? "")} ${yyyy ?? ""}`;

  const load = React.useCallback(() => {
    setLoading(true);
    const apiPeriod = period.replace("/", "-"); // path-safe "MM-YYYY"
    Promise.allSettled([
      call<{ totalRowCount: number; data: Partner[] }>("/api/v1/partners?active=true"),
      // Totali aggregati del periodo (crediti/incassato/saldo) — endpoint statistiche.
      call<PeriodSummary>(`/api/v1/payments/credits/statistics/${apiPeriod}`)
    ]).then(([partnersResult, summaryResult]) => {
      if (partnersResult.status === "fulfilled") {
        setPartnerCount(partnersResult.value.totalRowCount ?? partnersResult.value.data?.length ?? 0);
      }
      if (summaryResult.status === "fulfilled" && summaryResult.value) {
        const s = summaryResult.value;
        // SALDO della dashboard = residuo da incassare (crediti − incassato).
        setSummary({ ...s, balance: (s.totalCredits ?? 0) - (s.totalDebits ?? 0) });
      } else {
        setSummary(null);
      }
    }).finally(() => setLoading(false));
  }, [call, period]);

  useEffect(() => { load(); }, [load]);

  const fmtEur = (v?: number) =>
    v != null ? `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—";

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("home.title")}
        subtitle={t("home.currentPeriod", { period: currentPeriodLabel })}
        onRefresh={load}
        actions={
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("common.period")}</InputLabel>
            <Select
              label={t("common.period")}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}
            >
              {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        }
      />
      <Box sx={{ p: 3, flex: 1 }}>
        {/* KPI Row */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title={t("home.activePartners")}
              value={partnerCount ?? "—"}
              subtitle={t("home.activePartnersSub")}
              icon={<PeopleIcon fontSize="small" />}
              color="#2E7D32"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title={t("home.creditsPeriod")}
              value={summary ? fmtEur(summary.totalCredits) : "—"}
              subtitle={t("home.creditsPeriodSub")}
              icon={<TrendingUpIcon fontSize="small" />}
              color="#E65100"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title={t("home.collected")}
              value={summary ? fmtEur(summary.totalDebits) : "—"}
              subtitle={t("home.collectedSub")}
              icon={<PaymentIcon fontSize="small" />}
              color="#1565C0"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title={t("home.balance")}
              value={summary ? fmtEur(summary.balance) : "—"}
              subtitle={t("home.balanceSub")}
              icon={<BarChartIcon fontSize="small" />}
              color="#6A1B9A"
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Progress bar se c'è summary */}
        {summary && summary.totalCredits > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>{t("home.incomePeriod")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fmtEur(summary.totalDebits)} / {fmtEur(summary.totalCredits)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (summary.totalDebits / summary.totalCredits) * 100)}
                sx={{ height: 8, borderRadius: 4 }}
                color="success"
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="caption" color="success.main">
                  {t("home.settledLabel", { count: summary.settled })}
                </Typography>
                <Typography variant="caption" color="warning.main">
                  {t("home.unsettledLabel", { count: summary.unsettled })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Quick links */}
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: "text.secondary" }}>
          {t("home.quickAccess")}
        </Typography>
        <Grid container spacing={2}>
          {QUICK_LINKS.map((link) => (
            <Grid item xs={6} sm={4} md={3} key={link.path}>
              <Card sx={{ height: "100%" }}>
                <CardActionArea onClick={() => navigate(link.path)} sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Avatar sx={{ bgcolor: link.color + "18", width: 36, height: 36 }}>
                      <Box sx={{ color: link.color, display: "flex", fontSize: 20 }}>{link.icon}</Box>
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={700}>{t(link.titleKey)}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{t(link.descKey)}</Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
